import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, friendlyError } from "./supabase.js";
import { REGISTERS } from "./registers.js";
import { useLiveRows, useLiveTables, useFallbackPoll } from "./useLive.js";

/* Loading, saving and retiring rows for one register on one contract.
 *
 * Two things worth knowing about what this does and does not do.
 *
 * It never filters by contract for security. It passes contract_id so the
 * server returns the right rows, but the reason another contract's rows do
 * not appear is that the database refuses them. If this filter were removed
 * the result would still be correct.
 *
 * Deleting sets deleted_at rather than destroying the row. Anyone working a
 * contract can retire a row, which imports make necessary. Only the owner can
 * destroy one, and the database enforces that, not this file.
 */
export function useRegister(registerKey, contractId) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nudge = useRef(null);
  useEffect(() => () => clearTimeout(nudge.current), []);

  const sort = REGISTERS[registerKey]?.defaultSort ?? { column: "created_at", ascending: false };

  const load = useCallback(async () => {
    if (!contractId) { setRows([]); return; }
    const { data, error } = await supabase
      .from(registerKey)
      .select("*")
      .eq("contract_id", contractId)
      .is("deleted_at", null)
      .order(sort.column, { ascending: sort.ascending, nullsFirst: false });
    if (error) setError(friendlyError(error));
    else { setRows(data ?? []); setError(""); }
  }, [registerKey, contractId, sort.column, sort.ascending]);

  useEffect(() => { setRows(null); load(); }, [load]);

  /* Somebody else changed a row in this register. Registers are small, a few
     hundred rows at the most, so the honest answer is to fetch them again
     rather than to patch the list from the payload and slowly grow a second
     copy of the sort order and the filter. Coalesced, because a person working
     down a list produces a burst of changes and not one. */
  const live = useLiveRows(registerKey, contractId ? `contract_id=eq.${contractId}` : null,
    useCallback(() => {
      clearTimeout(nudge.current);
      nudge.current = setTimeout(load, 250);
    }, [load]));
  useFallbackPoll(load, live);

  const save = useCallback(async (draft) => {
    setBusy(true);
    setError("");
    const payload = { ...draft, contract_id: contractId };
    delete payload.created_at;
    delete payload.updated_at;
    delete payload.added_by;
    delete payload.updated_by;
    delete payload.risk_score;            // generated by the database
    delete payload.is_contract_deliverable;

    const { error } = draft.id
      ? await supabase.from(registerKey).update(payload).eq("id", draft.id)
      : await supabase.from(registerKey).insert(payload);

    setBusy(false);
    if (error) { setError(friendlyError(error)); return false; }
    await load();
    return true;
  }, [registerKey, contractId, load]);

  const retire = useCallback(async (id) => {
    setBusy(true);
    const { error } = await supabase
      .from(registerKey)
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);
    setBusy(false);
    if (error) { setError(friendlyError(error)); return false; }
    await load();
    return true;
  }, [registerKey, load]);

  return { rows, error, busy, reload: load, save, retire, live };
}

/* Several registers at once, for pages that need to combine them.
   Returns { key: rows } with null until everything has arrived. */
export function useRegisters(keys, contractId) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!contractId) { setData(Object.fromEntries(keys.map((k) => [k, []]))); return; }
    const results = await Promise.all(
      keys.map((k) =>
        supabase.from(k).select("*").eq("contract_id", contractId).is("deleted_at", null)
      )
    );
    const failed = results.find((r) => r.error);
    if (failed) { setError(friendlyError(failed.error)); return; }
    setData(Object.fromEntries(keys.map((k, i) => [k, results[i].data ?? []])));
    setError("");
  }, [keys.join(","), contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setData(null); load(); }, [load]);

  /* One subscription per table rather than one for the page, because the
     filter is server side and a page watching five registers should be woken
     by five kinds of change and no others. */
  const nudge = useRef(null);
  useEffect(() => () => clearTimeout(nudge.current), []);
  const live = useLiveTables(keys, contractId ? `contract_id=eq.${contractId}` : null,
    useCallback(() => {
      clearTimeout(nudge.current);
      nudge.current = setTimeout(load, 250);
    }, [load]));
  useFallbackPoll(load, live);

  return { data, error, reload: load, live };
}
